import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  CardHeader,
  Badge,
  Spinner,
  Alert,
  Modal,
  ModalFooter,
} from '@orientamos/ui'
import {
  Edit,
  Trash2,
  FileText,
  Building2,
  User,
  Users,
  Banknote,
  CheckCircle,
  XCircle,
  ArrowLeft,
} from 'lucide-react'
import { getIntake, deleteIntake, updateIntakeStatus, type IntakeDetail } from '@lib/api'
import { formatCurrency, formatDate } from '@orientamos/shared'

const statusBadgeVariants: Record<string, 'default' | 'warning' | 'success' | 'primary'> = {
  draft: 'warning',
  complete: 'primary',
  approved: 'success',
}

const statusLabels: Record<string, string> = {
  draft: 'Borrador',
  complete: 'Completo',
  approved: 'Aprobado',
}

const contractTypeLabels: Record<string, string> = {
  residential: 'Residencial',
  commercial: 'Comercial',
}

const propertyTypeLabels: Record<string, string> = {
  apartamento: 'Apartamento',
  casa: 'Casa',
  local: 'Local Comercial',
  oficina: 'Oficina',
  bodega: 'Bodega',
  lote: 'Lote',
  parqueadero: 'Parqueadero',
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-2 border-b border-neutral-100 last:border-0">
      <span className="text-neutral-500">{label}</span>
      <span className="text-neutral-900 font-medium text-right">{value || '-'}</span>
    </div>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader
        title={
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700">
              {icon}
            </div>
            {title}
          </div>
        }
      />
      <div className="mt-4">{children}</div>
    </Card>
  )
}

function PartyCard({ party, index }: { party: IntakeDetail['parties'][0]; index?: number }) {
  const name =
    party.personType === 'legal'
      ? party.companyName || `${party.firstName} ${party.lastName}`
      : `${party.firstName} ${party.lastName}`

  return (
    <div className="p-4 border border-neutral-200 rounded-lg">
      {index !== undefined && (
        <div className="text-xs text-neutral-500 mb-2">
          {party.role === 'owner' ? 'Propietario' : 'Deudor Solidario'} {index + 1}
        </div>
      )}
      <div className="font-medium text-neutral-900">{name}</div>
      <div className="text-sm text-neutral-500 mt-1">
        {party.personType === 'legal' ? 'Persona Juridica' : 'Persona Natural'} - {party.idNumber}
      </div>
      <div className="text-sm text-neutral-500 mt-1">{party.email}</div>
      <div className="text-sm text-neutral-500">{party.phone}</div>

      {party.banking && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <div className="text-xs font-medium text-neutral-700 mb-1">Datos Bancarios</div>
          <div className="text-sm text-neutral-600">
            {party.banking.bankName} - {party.banking.accountType}
          </div>
          <div className="text-sm text-neutral-600">Cuenta: {party.banking.accountNumber}</div>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {party.dataAuthorizationAccepted ? (
          <Badge variant="success" size="sm">
            <CheckCircle className="w-3 h-3 mr-1" />
            Datos autorizados
          </Badge>
        ) : (
          <Badge variant="danger" size="sm">
            <XCircle className="w-3 h-3 mr-1" />
            Sin autorizacion
          </Badge>
        )}
      </div>
    </div>
  )
}

export default function IntakeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [intake, setIntake] = useState<IntakeDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    async function loadIntake() {
      if (!id) return

      setIsLoading(true)
      setError(null)

      try {
        const result = await getIntake(id)
        if (result.success && result.data) {
          setIntake(result.data)
        } else {
          setError(result.error || 'Orden no encontrada')
        }
      } catch {
        setError('Error de conexion')
      } finally {
        setIsLoading(false)
      }
    }

    loadIntake()
  }, [id])

  const handleDelete = async () => {
    if (!id) return

    setIsDeleting(true)
    try {
      const result = await deleteIntake(id)
      if (result.success) {
        navigate('/intakes')
      }
    } catch (err) {
      console.error('Error deleting:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleStatusChange = async (status: 'draft' | 'complete' | 'approved') => {
    if (!id) return

    try {
      const result = await updateIntakeStatus(id, status)
      if (result.success) {
        setIntake((prev) => (prev ? { ...prev, status } : null))
      }
    } catch (err) {
      console.error('Error updating status:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !intake) {
    return (
      <div>
        <PageHeader
          title="Orden no encontrada"
          breadcrumbs={[
            { label: 'Inicio', href: '/' },
            { label: 'Ordenes de Contrato', href: '/intakes' },
            { label: 'Error' },
          ]}
        />
        <Alert variant="danger">{error || 'La orden solicitada no existe'}</Alert>
        <div className="mt-4">
          <Link to="/intakes">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver a la lista
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const tenant = intake.parties.find((p) => p.role === 'tenant')
  const guarantors = intake.parties.filter((p) => p.role === 'guarantor')
  const owners = intake.parties.filter((p) => p.role === 'owner')

  return (
    <div>
      <PageHeader
        title={`Orden ${intake.orderNumber}`}
        description={intake.property.address}
        breadcrumbs={[
          { label: 'Inicio', href: '/' },
          { label: 'Ordenes de Contrato', href: '/intakes' },
          { label: intake.orderNumber },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Link to={`/intakes/${id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-1" />
                Editar
              </Button>
            </Link>
            <Button
              variant="ghost"
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
              onClick={() => setDeleteModalOpen(true)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        }
      />

      {/* Status Bar */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-sm text-neutral-500">Estado actual:</span>
              <Badge variant={statusBadgeVariants[intake.status]} className="ml-2">
                {statusLabels[intake.status]}
              </Badge>
            </div>
            <div className="text-sm text-neutral-500">Creado: {formatDate(intake.createdAt)}</div>
          </div>
          <div className="flex gap-2">
            {intake.status === 'draft' && (
              <Button size="sm" onClick={() => handleStatusChange('complete')}>
                Marcar como Completo
              </Button>
            )}
            {intake.status === 'complete' && (
              <Button size="sm" onClick={() => handleStatusChange('approved')}>
                Aprobar Orden
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract Type */}
        <Section title="Tipo de Contrato" icon={<FileText className="w-4 h-4" />}>
          <DataRow label="Tipo" value={contractTypeLabels[intake.contractType]} />
          <DataRow
            label="Canon Garantizado"
            value={
              intake.guaranteedRent ? (
                <Badge variant="success" size="sm">
                  Si
                </Badge>
              ) : (
                <Badge variant="default" size="sm">
                  No
                </Badge>
              )
            }
          />
        </Section>

        {/* Property */}
        <Section title="Inmueble" icon={<Building2 className="w-4 h-4" />}>
          <DataRow label="Direccion" value={intake.property.address} />
          <DataRow label="Tipo" value={propertyTypeLabels[intake.property.propertyType]} />
          <DataRow label="Ciudad" value={intake.property.city} />
        </Section>

        {/* Tenant */}
        <Section title="Arrendatario" icon={<User className="w-4 h-4" />}>
          {tenant ? (
            <PartyCard party={tenant} />
          ) : (
            <p className="text-neutral-500">Sin arrendatario</p>
          )}
        </Section>

        {/* Guarantors */}
        <Section
          title={`Deudores Solidarios (${guarantors.length})`}
          icon={<Users className="w-4 h-4" />}
        >
          {guarantors.length === 0 ? (
            <p className="text-neutral-500">Sin deudores solidarios</p>
          ) : (
            <div className="space-y-3">
              {guarantors.map((g, i) => (
                <PartyCard key={g.id} party={g} index={i} />
              ))}
            </div>
          )}
        </Section>

        {/* Owners */}
        <Section title={`Propietarios (${owners.length})`} icon={<Building2 className="w-4 h-4" />}>
          <div className="space-y-3">
            {owners.map((o, i) => (
              <PartyCard key={o.id} party={o} index={i} />
            ))}
          </div>
        </Section>

        {/* Terms */}
        <Section title="Condiciones" icon={<Banknote className="w-4 h-4" />}>
          <DataRow label="Canon Mensual" value={formatCurrency(intake.terms.monthlyRent)} />
          <DataRow label="Administracion" value={formatCurrency(intake.terms.administrationFee)} />
          <DataRow label="Fecha de Inicio" value={formatDate(intake.terms.startDate)} />
          <DataRow label="Fecha de Fin" value={formatDate(intake.terms.endDate)} />
          <DataRow label="Duracion" value={`${intake.terms.durationMonths} meses`} />
          <DataRow label="Incremento" value={intake.terms.rentIncreaseFormula} />
        </Section>
      </div>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eliminar Orden"
        description="Esta accion no se puede deshacer"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas eliminar la orden{' '}
          <span className="font-medium text-neutral-900">{intake.orderNumber}</span>?
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
